// ZenFokusDiorama — Zone A links (AUFTRAG K2, docs/COCKPIT-SANIERUNG.md
// "ZenFokus v2" Runden 2–6 + COCKPIT-MASTERPLAN §1–§3).
//
// Prozeduraler Sumi-e-Baum (Seed = YYYYMMDD → wächst jeden Tag minimal
// anders), kontinuierliches Noise-Windfeld (EIN rAF-Loop, pausiert
// off-screen), 16 Wetterlagen aus dem Lage-Score (deriveWetter — DAS
// zentrale Signal, keine eigenen Schwellen), 30s-Parameter-Blend,
// Jahreszeit = Laub, Lage = Wind. Keine Buttons (nur ?wetter=-Debug);
// Klick auf das Wetter-Label öffnet die Faktor-Aufschlüsselung (Mono).
//
// Daten-Kontrakt (Masterplan §3, Zeile Baum/Wetter):
//   ok      → Live-Score, 30s-Blend
//   stale   → Sicht-Faktor treibt den Score ohnehin Richtung Nebel-Lagen
//   missing → Default BRISE + Mono-Hinweis
//   error   → Wetter friert ein (letzter Score bleibt), Mono-Hinweis
//
// Performance: nur transform/opacity; FX-Layer sind CSS-Animationen
// (zählen nicht als rAF-Loop); reduced-motion → fertiger Baum, statisch.

import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { fetchFokus, type FokusData } from "../../lib/api";
import {
  abschnittFor,
  deriveWetterLage,
  saisonFor,
  tagesSeed,
  zitatFor,
  type Saison,
  type WetterLage,
  type WetterScore,
} from "../../lib/derive-wetter";
import { subscribe } from "../../lib/motion/ticker";

// ----------------------------------------------------------------------
// Seeded RNG (mulberry32) + Baum-Generator (pure, einmal pro Tag/Seed)
// ----------------------------------------------------------------------

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

interface Ast {
  id: number;
  path: string;
  breite: number;
  tiefe: number;
  /** Fußpunkt (Rotationszentrum im Wind). */
  x: number;
  y: number;
  endX: number;
  endY: number;
  kinder: Ast[];
}

interface Blatt {
  x: number;
  y: number;
  r: number;
  phase: number;
}

const MAX_TIEFE = 6;

function generiereBaum(seed: number): { wurzel: Ast; blaetter: Blatt[] } {
  const rnd = mulberry32(seed);
  let nextId = 0;
  const blaetter: Blatt[] = [];

  const branch = (
    x: number,
    y: number,
    winkel: number,
    laenge: number,
    breite: number,
    tiefe: number,
  ): Ast => {
    const endX = x + Math.cos(winkel) * laenge;
    const endY = y - Math.sin(winkel) * laenge;
    // Quadratic-Bezier mit Seed-Jitter — der Sumi-e-Bogen.
    const cx = x + Math.cos(winkel) * laenge * 0.5 + (rnd() - 0.5) * laenge * 0.3;
    const cy = y - Math.sin(winkel) * laenge * 0.5 + (rnd() - 0.5) * laenge * 0.3;
    const ast: Ast = {
      id: nextId++,
      path: `M ${x.toFixed(1)} ${y.toFixed(1)} Q ${cx.toFixed(1)} ${cy.toFixed(1)} ${endX.toFixed(1)} ${endY.toFixed(1)}`,
      breite,
      tiefe,
      x,
      y,
      endX,
      endY,
      kinder: [],
    };
    if (tiefe < MAX_TIEFE) {
      const nKinder = tiefe === 0 ? 3 : rnd() < 0.65 ? 2 : 3;
      for (let i = 0; i < nKinder; i++) {
        const spread = 0.38 + rnd() * 0.3; // ±0.38–0.68 rad
        const richtung = i === 0 ? -1 : i === 1 ? 1 : rnd() < 0.5 ? -1 : 1;
        ast.kinder.push(
          branch(
            endX,
            endY,
            winkel + richtung * spread + (rnd() - 0.5) * 0.15,
            laenge * (0.68 + rnd() * 0.14),
            Math.max(0.5, breite * 0.62),
            tiefe + 1,
          ),
        );
      }
    } else {
      // Terminal-Ast: 2–3 Blätter mit Jitter.
      const n = 2 + (rnd() < 0.4 ? 1 : 0);
      for (let i = 0; i < n; i++) {
        blaetter.push({
          x: endX + (rnd() - 0.5) * 8,
          y: endY + (rnd() - 0.5) * 8,
          r: 1.6 + rnd() * 1.4,
          phase: rnd() * Math.PI * 2,
        });
      }
    }
    return ast;
  };

  const wurzel = branch(150, 250, Math.PI / 2, 62, 5.5, 0);
  return { wurzel, blaetter };
}

// ----------------------------------------------------------------------
// Saison-Farben (Laub) — Lage bestimmt WIND, Saison bestimmt LAUB.
// ----------------------------------------------------------------------

const LAUB: Record<Saison, { farben: string[]; dichte: number }> = {
  fruehling: { farben: ["#bef264", "#d9f99d", "#f0abfc"], dichte: 0.6 },
  sommer: { farben: ["#a3e635", "#84cc16", "#bef264"], dichte: 1 },
  herbst: { farben: ["#fb923c", "#ef4444", "#f59e0b"], dichte: 0.75 },
  winter: { farben: [], dichte: 0 },
};

// ----------------------------------------------------------------------
// Komponente
// ----------------------------------------------------------------------

interface Props {
  /** Live-Wetter aus deriveWetter — null solange Queries laden (missing). */
  wetter: WetterScore | null;
  /** true wenn eine Kern-Query im Error ist → Wetter friert ein. */
  fehler?: boolean;
  /** true wenn kritische Anomalien offen (→ Gewitterleuchten). */
  kritischeAnomalien?: boolean;
}

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

function debugOverride(): string | null {
  try {
    return new URLSearchParams(window.location.search).get("wetter");
  } catch {
    return null;
  }
}

export default function ZenFokusDiorama({ wetter, fehler, kritischeAnomalien }: Props) {
  const heute = new Date();
  const seed = tagesSeed(heute);
  const saison = saisonFor(heute.getMonth() + 1);
  const laub = LAUB[saison];

  const { wurzel, blaetter } = useMemo(() => generiereBaum(seed), [seed]);
  const [gewachsen, setGewachsen] = useState(false);
  const [faktorenOffen, setFaktorenOffen] = useState(false);

  // error-Kontrakt: letzten gültigen Score einfrieren.
  const letzterScore = useRef<WetterScore | null>(null);
  if (wetter && !fehler) letzterScore.current = wetter;
  const effektiv = fehler ? letzterScore.current : (wetter ?? letzterScore.current);

  const lage: WetterLage = deriveWetterLage({
    wetter: effektiv ?? {
      score: 25,
      familie: "mild",
      faktoren: {
        liquiditaet: 25,
        forderungsdruck: 0,
        handlungsdruck: 0,
        gegenwind: 0,
        sicht: 0,
      },
      dominanterFaktor: "liquiditaet",
    },
    tagesSeed: seed,
    kritischeAnomalien,
    override: debugOverride(),
  });

  // Winter: Regen wird Schnee, Falter entfallen (→ leichte Brise-Optik).
  const fxLage =
    saison === "winter" && lage.id === "falter" ? "leichte_brise" : lage.id;
  const schnee = saison === "winter" && (fxLage === "erste_tropfen" || fxLage === "platzregen");

  // Fokus-Text (Autosuggestion-Abschnitt + Zitat + Countdown).
  const fokusQuery = useQuery<FokusData, Error>({
    queryKey: ["fokus"],
    queryFn: fetchFokus,
    staleTime: 10 * 60_000,
    retry: 1,
  });
  const abschnitt = fokusQuery.data
    ? abschnittFor(fokusQuery.data.autosuggestion, seed)
    : null;
  const zitat = zitatFor(lage.familie, seed);
  const zielDatum = fokusQuery.data?.countdown_datum;
  const tageBis =
    zielDatum != null
      ? Math.max(
          0,
          Math.ceil((new Date(`${zielDatum}T23:59:59`).getTime() - Date.now()) / 86_400_000),
        )
      : null;

  // ------------------------------------------------------------------
  // Wind-Loop (der EINE rAF-Loop der Seite; pausiert off-screen).
  // ------------------------------------------------------------------
  const containerRef = useRef<HTMLDivElement | null>(null);
  const astRefs = useRef(new Map<number, { el: SVGGElement; ast: Ast }>());
  const params = useRef({ amp: 0.16, gust: 0.2, lean: 0 });
  const ziel = useRef({ amp: lage.amp, gust: lage.gustAmp, lean: lage.lean });
  ziel.current = { amp: lage.amp, gust: lage.gustAmp, lean: lage.lean };

  useEffect(() => {
    setGewachsen(true);
    if (prefersReducedMotion()) {
      // Statischer Endzustand: Params sofort, kein Loop.
      params.current = { ...ziel.current };
      return;
    }

    let sichtbar = true;
    let letztesT = performance.now();

    const observer = new IntersectionObserver(([entry]) => {
      sichtbar = entry.isIntersecting;
    });
    if (containerRef.current) observer.observe(containerRef.current);

    // K-INTEGRATION §3 / Masterplan §7: KEIN eigener rAF — der Baum-Wind läuft
    // am zentralen Ticker (lib/motion/ticker.ts, EINE Loop fürs ganze Cockpit,
    // pausiert bei document.hidden + reduced-motion). Off-screen pausiert
    // zusätzlich der IntersectionObserver.
    const tick = (now: number) => {
      if (!sichtbar) {
        letztesT = now;
        return;
      }
      const dt = Math.min(0.1, (now - letztesT) / 1000);
      letztesT = now;
      const t = now / 1000;

      // 30s-Blend auf neue Wetter-Parameter (kein Nervös-Flackern).
      const p = params.current;
      const z = ziel.current;
      const k = Math.min(1, dt / 30);
      p.amp += (z.amp - p.amp) * k;
      p.gust += (z.gust - p.gust) * k;
      p.lean += (z.lean - p.lean) * k;

      // Böen-Hüllkurve (0..1) — gemeinsame Front für alle Äste.
      const env = Math.max(0, 0.6 * Math.sin(t * 0.23) + 0.5 * Math.sin(t * 0.11) - 0.1);
      const orkanZittern = p.amp > 0.9 ? Math.sin(t * 18) * 0.15 * env : 0;

      for (const { el, ast } of astRefs.current.values()) {
        const phase = ast.id * 0.7 + ast.x * 0.011; // Welle läuft von links
        const noise =
          0.5 * Math.sin(t * 0.7 + phase) +
          0.3 * Math.sin(t * 1.7 + phase * 1.3) +
          0.2 * Math.sin(t * 3.1 + phase * 2.1);
        const flex = 0.15 + ast.tiefe * ast.tiefe * 0.1; // Stamm starr, Zweige weich
        const deg =
          noise * (p.amp + p.gust * env) * flex * 6 +
          (ast.tiefe === 0 ? p.lean + orkanZittern : 0);
        el.setAttribute("transform", `rotate(${deg.toFixed(2)} ${ast.x} ${ast.y})`);
      }
    };
    const unsub = subscribe(tick);
    return () => {
      unsub();
      observer.disconnect();
    };
  }, []);

  // Rekursives Rendering: verschachtelte <g> = hierarchisch-kumulatives Biegen.
  const renderAst = (ast: Ast): JSX.Element => (
    <g
      key={ast.id}
      ref={(el) => {
        if (el) astRefs.current.set(ast.id, { el, ast });
        else astRefs.current.delete(ast.id);
      }}
    >
      <path
        d={ast.path}
        fill="none"
        stroke="#8a8378"
        strokeWidth={ast.breite}
        strokeLinecap="round"
        pathLength={1}
        strokeDasharray={1}
        strokeDashoffset={gewachsen ? 0 : 1}
        style={{
          transition: `stroke-dashoffset 0.6s ease-out ${ast.tiefe * 0.26}s`,
        }}
      />
      {ast.kinder.map(renderAst)}
    </g>
  );

  const sichtbareBlaetter = Math.round(blaetter.length * laub.dichte);
  const wetterHinweis = fehler
    ? "WETTER EINGEFROREN — QUELLE GESTÖRT"
    : !wetter
      ? "LAGE UNVOLLSTÄNDIG — WARTE AUF DATEN"
      : null;

  return (
    <div
      ref={containerRef}
      className="relative h-full min-h-[300px] overflow-hidden rounded-2xl border border-leitstand-hairline bg-leitstand-panel"
      aria-label={`Zen-Fokus — Wetterlage ${lage.label}`}
    >
      <svg viewBox="0 0 300 300" className="absolute inset-0 h-full w-full" aria-hidden>
        <WetterFx lage={fxLage} schnee={schnee} />
        {/* Boden-Tuschestrich + Grasstriche (Sumi-e). */}
        <path d="M 40 251 Q 150 246 262 252" fill="none" stroke="#8a8378" strokeWidth={2} strokeLinecap="round" opacity={0.7} />
        {[70, 95, 210, 235].map((x) => (
          <path key={x} d={`M ${x} 251 q 2 -7 5 -9`} fill="none" stroke="#8a8378" strokeWidth={0.8} opacity={0.45} />
        ))}
        {renderAst(wurzel)}
        {/* Blätter: poppen nach ihrem Ast, schweben im Idle (CSS). */}
        {blaetter.slice(0, sichtbareBlaetter).map((b, i) => (
          <circle
            key={i}
            cx={b.x}
            cy={b.y}
            r={b.r}
            fill={laub.farben[i % Math.max(1, laub.farben.length)] ?? "#a3e635"}
            opacity={0.85}
            className="zen-blatt"
            style={{
              transformOrigin: `${b.x}px ${b.y}px`,
              transform: gewachsen ? "scale(1)" : "scale(0)",
              transition: `transform 0.45s cubic-bezier(0.34, 1.45, 0.64, 1) ${MAX_TIEFE * 0.26 + (i % 10) * 0.05}s`,
              animationDelay: `${(b.phase * 1.1).toFixed(2)}s`,
            }}
          />
        ))}
      </svg>

      {/* Text-Schicht: Autosuggestion-Abschnitt + Zitat + Countdown. */}
      <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-[#0b0b0c]/85 to-transparent">
        {abschnitt && (
          <p className="font-voice italic text-[13px] leading-snug text-leitstand-text/90 line-clamp-2">
            „{abschnitt}“
          </p>
        )}
        <p className="mt-1 text-[10px] text-leitstand-dim line-clamp-1">{zitat}</p>
        <div className="mt-1.5 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => setFaktorenOffen((v) => !v)}
            className="num-mono text-[10px] text-leitstand-dim hover:text-leitstand-text"
            aria-expanded={faktorenOffen}
          >
            WETTER: {lage.label}
            {effektiv ? ` · LAGE ${effektiv.score}` : ""}
          </button>
          {tageBis != null && (
            <span className="num-mono text-[10px] text-leitstand-dim">
              {tageBis.toLocaleString("de-DE")} TAGE BIS {zielDatum?.slice(0, 4) ?? "2030"}
            </span>
          )}
        </div>
        {wetterHinweis && (
          <p className="num-mono mt-1 text-[9px] text-signal-amber">{wetterHinweis}</p>
        )}
        {faktorenOffen && effektiv && (
          <dl className="num-mono mt-1.5 grid grid-cols-2 gap-x-4 gap-y-0.5 text-[9px] text-leitstand-dim">
            <div className="flex justify-between"><dt>LIQUIDITÄT</dt><dd>{Math.round(effektiv.faktoren.liquiditaet)}</dd></div>
            <div className="flex justify-between"><dt>FORDERUNGEN</dt><dd>{Math.round(effektiv.faktoren.forderungsdruck)}</dd></div>
            <div className="flex justify-between"><dt>HANDLUNGSDRUCK</dt><dd>{Math.round(effektiv.faktoren.handlungsdruck)}</dd></div>
            <div className="flex justify-between"><dt>GEGENWIND</dt><dd>{Math.round(effektiv.faktoren.gegenwind)}</dd></div>
            <div className="flex justify-between"><dt>SICHT</dt><dd>{Math.round(effektiv.faktoren.sicht)}</dd></div>
          </dl>
        )}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// Wetter-FX — Signatur-Elemente je Lage, reine CSS-Animationen
// (zählen nicht gegen das 2-rAF-Budget; Masterplan §6).
// ----------------------------------------------------------------------

function WetterFx({ lage, schnee }: { lage: string; schnee: boolean }) {
  switch (lage) {
    case "windstille":
    case "morgentau":
      return (
        <g aria-hidden>
          {[
            [60, 60], [220, 40], [120, 90], [250, 110], [40, 140],
          ].map(([x, y], i) => (
            <circle key={i} cx={x} cy={y} r={1} fill="#f5f1e8" className="zen-funkeln" style={{ animationDelay: `${i * 0.8}s` }} />
          ))}
        </g>
      );
    case "sonnenstrahlen":
      return (
        <g aria-hidden opacity={0.1}>
          <polygon points="180,0 220,0 120,300 90,300" fill="#f5f1e8" />
          <polygon points="240,0 265,0 190,300 170,300" fill="#f5f1e8" />
        </g>
      );
    case "vogelflug":
      return (
        <g aria-hidden className="zen-voegel">
          {[0, 1, 2].map((i) => (
            <path key={i} d={`M ${-20 - i * 18} ${50 + i * 12} q 3 -3 6 0 q 3 -3 6 0`} fill="none" stroke="#a8a29a" strokeWidth={1} />
          ))}
        </g>
      );
    case "wolkenschatten":
      return <ellipse aria-hidden cx={0} cy={80} rx={90} ry={30} fill="#0b0b0c" opacity={0.25} className="zen-wolke" />;
    case "falter":
      return (
        <g aria-hidden>
          <circle cx={100} cy={120} r={2} fill="#f0abfc" className="zen-falter" />
          <circle cx={200} cy={150} r={2} fill="#fcd34d" className="zen-falter" style={{ animationDelay: "1.4s" }} />
        </g>
      );
    case "erste_tropfen":
    case "platzregen": {
      const dicht = lage === "platzregen";
      const n = dicht ? 26 : 8;
      return (
        <g aria-hidden opacity={0.55}>
          {Array.from({ length: n }).map((_, i) => {
            const x = (i * 37) % 300;
            return schnee ? (
              <circle key={i} cx={x} cy={-5} r={1.3} fill="#f5f1e8" className="zen-schnee" style={{ animationDelay: `${(i % 9) * 0.35}s` }} />
            ) : (
              <line key={i} x1={x} y1={-8} x2={x - 3} y2={4} stroke="#94a3b8" strokeWidth={0.8} className="zen-regen" style={{ animationDelay: `${(i % 7) * 0.22}s`, animationDuration: dicht ? "0.7s" : "1.4s" }} />
            );
          })}
        </g>
      );
    }
    case "nebelbank":
      return (
        <g aria-hidden>
          <rect x={-140} y={150} width={260} height={44} rx={22} fill="#a8a29a" opacity={0.14} className="zen-nebel" />
          <rect x={-220} y={195} width={320} height={50} rx={25} fill="#a8a29a" opacity={0.1} className="zen-nebel" style={{ animationDelay: "6s" }} />
        </g>
      );
    case "blaetterwirbel":
      return (
        <g aria-hidden className="zen-wirbel" style={{ transformOrigin: "220px 240px" }}>
          {[0, 1, 2, 3, 4].map((i) => (
            <circle key={i} cx={220 + 14 * Math.cos((i / 5) * Math.PI * 2)} cy={240 + 8 * Math.sin((i / 5) * Math.PI * 2)} r={1.6} fill="#fb923c" opacity={0.7} />
          ))}
        </g>
      );
    case "gewitterleuchten":
      return (
        <g aria-hidden className="zen-blitz">
          <rect x={0} y={0} width={300} height={300} fill="#f5f1e8" opacity={0} />
          <path d="M 210 20 l -14 34 h 10 l -16 36" fill="none" stroke="#f5f1e8" strokeWidth={1.2} opacity={0} />
        </g>
      );
    case "sturm":
    case "orkan":
      return (
        <g aria-hidden opacity={0.4}>
          {[70, 130, 190].map((y, i) => (
            <path key={y} d={`M -60 ${y} q 40 -6 80 0 t 80 0`} fill="none" stroke="#a8a29a" strokeWidth={0.8} className="zen-windlinie" style={{ animationDelay: `${i * 0.9}s` }} />
          ))}
        </g>
      );
    default:
      return null;
  }
}
